import { useCallback, useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";

interface PlantillaMeta {
  id: number;
  fileUrl: string;
  updatedAt: string;
}

export function CertificadoPlantillaPage() {
  const [meta, setMeta] = useState<PlantillaMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewBlobRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMeta = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get<PlantillaMeta>("/admin/certificado-plantilla");
      setMeta(res.data);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) setMeta(null);
      else setError("No se pudo cargar el estado de la plantilla");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    if (!meta) {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/admin/certificado-plantilla/preview", { responseType: "blob" });
        if (cancelled) return;
        if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
        const u = URL.createObjectURL(res.data as Blob);
        previewBlobRef.current = u;
        setPreviewUrl(u);
      } catch {
        if (!cancelled) {
          if (previewBlobRef.current) {
            URL.revokeObjectURL(previewBlobRef.current);
            previewBlobRef.current = null;
          }
          setPreviewUrl(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta]);

  useEffect(() => {
    return () => {
      if (previewBlobRef.current) {
        URL.revokeObjectURL(previewBlobRef.current);
        previewBlobRef.current = null;
      }
    };
  }, []);

  const handleUpload = async (file: File) => {
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/certificado-plantilla", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      const status = ax.response?.status;
      const msg = ax.response?.data?.error;
      if (status === 403) {
        setUploadError(
          "No tienes permisos para subir la plantilla. Cierra sesión y vuelve a entrar con una cuenta de administrador."
        );
      } else {
        setUploadError(msg ?? "Error al subir la plantilla");
      }
      return;
    } finally {
      setUploading(false);
    }

    await fetchMeta();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setUploadError("Selecciona un PDF");
      return;
    }
    if (file.type !== "application/pdf") {
      setUploadError("Solo PDF");
      return;
    }
    await handleUpload(file);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Plantilla de certificado PDF</h1>
      <p className="text-sm text-gray-600">
        Sube un PDF con un campo de formulario AcroForm de texto llamado <strong>info</strong>. El texto del certificado se
        insertará ahí al generar certificados de pilotos.
      </p>

      {loading && <p className="text-gray-500 text-sm">Cargando…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !meta && (
        <p className="text-amber-800 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          No hay plantilla configurada. Los certificados se generan con el diseño por defecto del sistema.
        </p>
      )}

      {!loading && meta && (
        <div className="text-sm text-gray-700 space-y-1">
          <p>
            <span className="font-medium">Plantilla activa</span> (id {meta.id})
          </p>
          <p>
            R2: <code className="text-xs bg-gray-100 px-1 rounded">{meta.fileUrl}</code>
          </p>
          <p>Actualizada: {new Date(meta.updatedAt).toLocaleString("es-ES")}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3 border border-gray-200 rounded-lg p-4 bg-white">
        <label className="block text-sm font-medium text-gray-700">Subir nueva plantilla (PDF, máx. 20MB)</label>
        <input
          ref={fileInputRef}
          name="file"
          type="file"
          accept=".pdf,application/pdf"
          className="block w-full text-sm"
        />
        {uploadError && <p className="text-red-600 text-sm">{uploadError}</p>}
        <Button type="submit" loading={uploading}>
          Guardar plantilla
        </Button>
      </form>

      {previewUrl && (
        <div className="space-y-2">
          <h2 className="text-lg font-medium text-[#2C2C2C]">Vista previa (datos de ejemplo)</h2>
          <iframe title="Vista previa certificado" src={previewUrl} className="w-full min-h-[720px] border border-gray-200 rounded-lg bg-white" />
        </div>
      )}
    </div>
  );
}
